import axios from 'axios';
import Cookies from 'js-cookie';

const getConfig = () => {
    const token = Cookies.get('jwt');
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    return config;
};

const getBlobHeadersConfig = () => {
    const token = Cookies.get('jwt');
    const config = {
        headers: {
            Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
    };
    return config as any;
};

export const getConsultation = async (consultationId: string) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/${consultationId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving a consultation:', error);
    }
};

interface UpdateConsultationProps {
    consultationId: string | undefined;
    isConsentGiven?: boolean;
    isConsentRemoved?: boolean;
    language?: string | undefined;
    noteType?: string; // Added for consistency
};

export const updateConsultation = async (content: UpdateConsultationProps) => {
    try {
        const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/consultation/${content.consultationId}`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error updating a consultation:', error);
    }
};

export const getConsultations = async (patientId: string) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/patient/${patientId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving a list of consultations:', error);
    }
};

export const getConsultationsList = async () => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/consultations/list`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving a list of active consultations:', error);
    }
};

export const getRecentNotes = async () => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/recent/notes`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving recent notes. getRecentNotes().');
    };
};

export const createConsultation = async () => {
    try {
        const content = {};
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error creating a consultation:', error);
    }
};

export const saveConsultation = async (consultationId: string) => {
    try {
        const content = {
            consultationId: consultationId
        };
        const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/consultation`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error saving a consultation:', error);
    }
};

export const deleteConsultation = async (consultationId: string) => {
    try {
        const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/consultation/${consultationId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error deleting a consultation:', error);
    };
};

export const getConsultationNote = async (consultationNoteId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/${consultationNoteId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving a consultation note.', error);
    };
};

export const getNoteTemplates = async () => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/templates/list`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving note templates.', error);
    };
};

export const getConsultationNotes = async (consultationId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/notes/${consultationId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving the consultation notes.', error);
    };
};

interface ConsultationNoteProps {
    patientId?: string;
    consultationId?: string;
    type?: string;
}

export const createConsultationNote = async (content: ConsultationNoteProps) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/note`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error creating a consultation note:', error);
    }
};

export const createNoteWithTemplate = async (noteTemplateId: string, consultationId: string) => {
    try {
        const content = {
            consultationId: consultationId
        };
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/templates/${noteTemplateId}`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error creating a consultation note with a template:', error);
    }
};

interface EditConsultationNoteProps {
    consultationNoteId: string | undefined;
    mode: string | undefined;
    newDescription?: string;
    overWrite?: boolean;
    securityPin: string | undefined;
    attribute?: string;
    newField?: string;
    selectionStart?: number;
    selectedNoteFieldId?: string;
};

export const editConsultationNote = async (content: EditConsultationNoteProps) => {
    try {
        const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/note/${content.consultationNoteId}`, content);
        return res;
    } catch (error) {
        console.error('Error editing a consultation note:', error);
    }
};

interface UpdateConsultationNoteProps {
    consultationNoteId: string | undefined;
    securityPin: string | undefined;
    title?: string | undefined;
    description?: string | undefined;
    blankDescription?: boolean;
    removeRefresh?: boolean;
    startRecording?: boolean;
    stopRecording?: boolean;
    micType?: string | undefined;
    audioTranscription?: string;
    additionalTranscription?: string;
};

export const updateConsultationNote = async (content: UpdateConsultationNoteProps) => {
    try {
        const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/user/note/${content.consultationNoteId}`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error updating a consultation note:', error);
    }
};

export const getAutoComplete = async (formData: any, consultationNoteId: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/autoComplete/${consultationNoteId}`, formData, getBlobHeadersConfig());
        return res;
    } catch (error) {
        console.error('Error rendering the auto complete', error);
    };
};

export const getConsultationLanguage = async (consultationNoteId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/note/language/${consultationNoteId}`);
        return res;
    } catch (error) {
        console.error('Error retrieving the consultation note default language:', error);
    }
};

export const serveConsultationNote = async (consultationNoteId?: string) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/serve/${consultationNoteId}`, getBlobHeadersConfig());
        return res;
    } catch (error) {
        console.error('Error serving a consultation note.', error);
    };
};

export const getLastNoteByConsultationId = async (consultationId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/last/${consultationId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving the last note with consultation id.', error);
    };
};

export const getConsultationWithPin = async (consultationId: any, securityPin: string) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/data/${consultationId}/${securityPin}`);
        return res;
    } catch (error) {
        console.error('Error when trying to get a consultation with PIN.', error);
    };
};

export const getConsultationNoteWithPin = async (consultationNoteId: any, securityPin: string) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/data/note/${consultationNoteId}/${securityPin}`);
        return res;
    } catch (error) {
        console.error('Error when trying to access a consultation note with PIN.', error);
    };
};

export const saveConsultationNote = async (formData: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/save`, formData, getConfig());
        return res;
    } catch (error) {
        console.error('Error saving a consultation note.', error);
    };
};

export const duplicateTemplate = async (consultationNoteId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/duplicate/${consultationNoteId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error duplicating a template:', error);
    }
};

export const getNoteFields = async (consultationNoteId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/fields/${consultationNoteId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving note fields', error);
    };
};

export const getNoteFieldsWithPin = async (consultationNoteId: any, securityPin: string) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/note/fields/${consultationNoteId}/${securityPin}`);
        return res;
    } catch (error) {
        console.error('Error retrieving note fields with PIN', error);
    };
};

export const deleteConsultationNote = async (consultationNoteId: any) => {
    try {
        const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/${consultationNoteId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error deleting a consultation note:', error);
    };
};

export const getAttachedFiles = async (consultationId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/files/${consultationId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving the consultation files.', error);
    };
};

export const attachFileToConsultation = async (formData: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/file/attach`, formData, getBlobHeadersConfig());
        return res;
    } catch (error) {
        console.error('Error attaching a file to the consultation.', error);
    };
};

export const editAttachedFile = async (fileId: any, title: any) => {
    try {
        const content = {
            title: title
        };
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/file/edit/${fileId}`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error editiing a file.', error);
    };
};

export const deleteAttachedFile = async (attachedFileId: string) => {
    try {
        const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/consultation/file/${attachedFileId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error deleting an attached file.', error);
    };
};

export const serveConsultationImage = async (imageId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/image/${imageId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error serving an image.', error);
    };
};

export const getConsultationImages = async (consultationId: any) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/images/${consultationId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error retrieving the consultation images.', error);
    };
};

export const uploadImageToConsultation = async (formData: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/image`, formData, getBlobHeadersConfig());
        return res;
    } catch (error) {
        console.error('Error attaching an images to the consultation.', error);
    };
};

export const uploadImageWithPin = async (formData: any, consultationId: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/user/consultation/image/${consultationId}`, formData, getBlobHeadersConfig());
        return res;
    } catch (error) {
        console.error('Error uploading an image with PIN', error);
    };
};

export const uploadNotePreview = async (formData: any, consultationNoteId: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/preview/${consultationNoteId}`, formData, getBlobHeadersConfig());
        return res;
    } catch (error) {
        console.error('Error uploading a note preview', error);
    };
};

export const deleteConsultationImage = async (imageId: string) => {
    try {
        const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/consultation/image/${imageId}`, getConfig());
        return res;
    } catch (error) {
        console.error('Error deleting an attached image.', error);
    };
};

export const transcribeAudio = async (formData: any) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/user/transcribe`, formData);
        return res;
    } catch (error) {
        console.error('Error transcribing audio.', error);
    };
};

// FIX: Add the new function for sending a fax
export const sendFax = async (formData: FormData) => {
    try {
        // Axios will automatically set the 'Content-Type' to 'multipart/form-data'
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/note/fax`, formData, getConfig());
        return res;
    } catch (error) {
        console.error('Error sending fax.', error);
        // Re-throw the error so the component can catch it and display a message
        throw error;
    };
};


// THE FIX IS HERE: Updated function signature and request body
export const generateNote = async (consultationId: any, text: any, noteTypeId: string) => {
    try {
        const content = {
            consultationId: consultationId,
            transcribedText: text,
            noteType: noteTypeId, // Added the missing field
        };
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/consultation/generate/note`, content, getConfig());
        return res;
    } catch (error) {
        console.error('Error generating note:', error);
    };
};

export const apiGetNoteTypes = async () => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultation/note-types`, getConfig());
        return res;
    } catch (error) {
        console.error('Error getting note types:', error);
        throw error;
    }
};

export const generateNoteTitle = async (consultationId: string, text: string) => {
    try {
        const res = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/consultation/generate/title`,
            { consultationId, text },
            getConfig()
        );
        return res;
    } catch (error) {
        console.error('Error generating note title.', error);
    }
};